import React, { useMemo, useCallback, useRef } from 'react'
import { Formik, useFormikContext } from 'formik'
import { cloneDeep, get, set } from 'lodash'
import { parse } from 'date-fns'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import Grid from '@mui/material/Grid'

import Modal from 'components/common/Modal'
import useNotification from 'hooks/context/useNotification'
import AddressAutocomplete from 'components/common/AddressAutocomplete'
import DateRangePicker from 'components/common/DateRangePicker'
import JsonEditor from 'components/preferences/JsonEditor'
import useAddressOverrides from 'hooks/preferences/useAddressOverrides'
import excludeKeys from 'helpers/node/excludeKeys'

const validationSchema = yup.object().shape({
  date_start: yup.date().nullable(),
  date_end: yup.date().nullable(),
  rule: yup.object().shape({
    address: yup.object().shape({
      addr1: yup.string().required('Address 1 is required'),
      city: yup.string().required('City is required'),
      state: yup.string().required('State is required'),
      postal_code: yup.string().required('Zip is required'),
      country: yup.string().required('Country is required'),
    }),
    mode: yup.string().required('Mode is required'),
    path: yup.string().required('Path is required'),
    value: yup.string().required('Value is required'),
  }),
})

const AddressSection = ({ show = true, hideLabel = false, ...props }) => {
  const { values, handleChange, errors, setErrors } = useFormikContext()

  const handleUpdateChange = useCallback(
    (e) => {
      handleChange(e)

      const newErrors = { ...errors }

      set(newErrors, e.target.name, undefined)

      setErrors(newErrors)
    },
    [handleChange, errors, setErrors]
  )

  return (
    <>
      <Box>
        {props.label && (
          <Typography variant="h2" sx={{ mb: 4 }}>
            {props.label}
          </Typography>
        )}
        <Grid container spacing={2} rowSpacing={3}>
          <Grid item xs={12}>
            <Typography variant="h2" sx={{ mb: 1 }}>
              Address Override
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Address name"
              variant="outlined"
              value={get(values, props.addressKeys.name)}
              onChange={handleUpdateChange}
              name={props.addressKeys.name}
              error={Boolean(get(errors, props.addressKeys.name))}
              helperText={Boolean(get(errors, props.addressKeys.name))}
              inputProps={{
                maxLength: 47,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <AddressAutocomplete {...props} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Address 2"
              variant="outlined"
              value={get(values, props.addressKeys.address2)}
              onChange={handleUpdateChange}
              name={props.addressKeys.address2}
              error={Boolean(get(errors, props.addressKeys.address2))}
              helperText={Boolean(get(errors, props.addressKeys.address2))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              size="small"
              label="City"
              variant="outlined"
              value={get(values, props.addressKeys.city)}
              onChange={handleUpdateChange}
              name={props.addressKeys.city}
              error={Boolean(get(errors, props.addressKeys.city))}
              helperText={get(errors, props.addressKeys.city)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              size="small"
              label="State"
              variant="outlined"
              value={get(values, props.addressKeys.state)}
              onChange={handleUpdateChange}
              name={props.addressKeys.state}
              error={Boolean(get(errors, props.addressKeys.state))}
              helperText={get(errors, props.addressKeys.state)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              size="small"
              label="Zip"
              variant="outlined"
              value={get(values, props.addressKeys.zip)}
              onChange={handleUpdateChange}
              name={props.addressKeys.zip}
              error={Boolean(get(errors, props.addressKeys.zip))}
              helperText={get(errors, props.addressKeys.zip)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              size="small"
              label="Country"
              variant="outlined"
              value={get(values, props.addressKeys.country)}
              onChange={handleUpdateChange}
              name={props.addressKeys.country}
              error={Boolean(get(errors, props.addressKeys.country))}
              helperText={get(errors, props.addressKeys.country)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

function EditAddressOverrideModal({ open, setOpen, addressOverride, mutate }) {
  const formikRef = useRef()
  const { setError, setBasicNotification } = useNotification()
  const { createRule, updateRule } = useAddressOverrides({})

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const { ruleDetail, ...rest } = values
        const updatedValues = cloneDeep(rest)

        if (ruleDetail === 'program_id') {
          updatedValues.rule.path = 'program_id'
        }

        const rule = addressOverride
          ? await updateRule(addressOverride.id, updatedValues)
          : await createRule(updatedValues)

        mutate()
        setOpen(false)
        setBasicNotification(
          `Address Override #${rule.id} has been successfully ${
            addressOverride ? 'updated' : 'created'
          }!`
        )
      } catch (err) {
        setError(
          err?.response?.data?.display_message ||
            err?.message ||
            `Unable to ${
              addressOverride ? 'update' : 'create'
            } address override`
        )
      }
    },
    [addressOverride, mutate, setOpen, setBasicNotification, setError]
  )

  const initialValues = useMemo(() => {
    return {
      ruleDetail:
        addressOverride?.rule?.path === 'program_id'
          ? 'program_id'
          : 'metadata',
      date_start: addressOverride?.date_start
        ? parse(addressOverride.date_start, 'yyyy-MM-dd', new Date())
        : undefined,
      date_end: addressOverride?.date_end
        ? parse(addressOverride.date_end, 'yyyy-MM-dd', new Date())
        : undefined,
      rule: addressOverride?.rule ?? {
        mode: 'match',
        path: '',
        value: '',
        address: {
          addr_name: addressOverride?.addr_name ?? '',
          addr1: addressOverride?.addr1 ?? '',
          addr2: addressOverride?.addr2 ?? '',
          city: addressOverride?.city ?? '',
          state: addressOverride?.state ?? '',
          postal_code: addressOverride?.postal_code ?? '',
          country: addressOverride?.country ?? 'USA',
        },
      },
    }
  }, [addressOverride])

  return (
    <Modal
      open={open}
      onClose={() => {}}
      height="90vh"
      width="732px"
      title={
        addressOverride
          ? `Edit Address Override #${addressOverride.id}`
          : 'Add New Address Override'
      }
      scrollable
      footerButtonProps={[
        {
          children: 'Save',
          variant: 'contained',
          color: 'primary',
          onClick: () => formikRef.current.submitForm(),
        },
        {
          children: 'Cancel',
          variant: 'outlined',
          color: 'primary',
          onClick: () => setOpen(false),
          popoverMessage: (
            <>
              Any changes you have made will not be saved.
              <br />
              Are you sure you want to cancel?
            </>
          ),
          popoverProps: {
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          },
          popover: true,
          cancelButtonProps: { children: 'Continue Editing' },
          acceptButtonProps: { children: 'Discard Changes' },
        },
      ]}
    >
      <Formik
        enableReinitialize
        innerRef={formikRef}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ handleChange, values, errors }) => (
          <Box sx={{ pb: 12, pt: 2 }}>
            <Stack spacing={3}>
              <TextField
                required
                fullWidth
                size="small"
                label="Rule Detail"
                variant="outlined"
                select
                value={values.ruleDetail}
                onChange={(e) => {
                  handleChange({
                    target: { name: 'ruleDetail', value: e.target.value },
                  })

                  handleChange({
                    target: {
                      name: 'rule.path',
                      value: e.target.value === 'metadata' ? '' : 'program_id',
                    },
                  })
                }}
              >
                <MenuItem value="metadata">Metadata</MenuItem>
                <MenuItem value="program_id">Program ID</MenuItem>
              </TextField>
              <JsonEditor
                label="Rule"
                value={excludeKeys(values.rule, ['address'])}
                onChange={(val) => {
                  handleChange({
                    target: {
                      name: 'rule',
                      value: { ...val, address: values.rule?.address },
                    },
                  })
                }}
                config={{
                  ruleDetail: values.ruleDetail,
                }}
                errors={errors}
              />
              <AddressSection
                addressKeys={{
                  name: 'rule.address.addr_name',
                  address: 'rule.address.addr1',
                  address2: 'rule.address.addr2',
                  city: 'rule.address.city',
                  state: 'rule.address.state',
                  zip: 'rule.address.postal_code',
                  country: 'rule.address.country',
                }}
                textFieldProps={{
                  size: 'small',
                }}
              />
              <Box pt={3}>
                <Typography variant="h2" mb={4}>
                  Start to End Date (Optional)
                </Typography>
                <Stack spacing={4}>
                  <DateRangePicker
                    label="Start Date"
                    name="date_start"
                    single
                    value={[values.date_start, undefined]}
                    setValue={(val) => {
                      const [dateStart] = val

                      handleChange({
                        target: {
                          name: 'date_start',
                          value: dateStart,
                        },
                      })
                    }}
                    size="small"
                    variant="outlined"
                  />
                  <DateRangePicker
                    label="End Date"
                    name="date_end"
                    single
                    value={[values.date_end, undefined]}
                    setValue={(val) => {
                      const [dateEnd] = val

                      handleChange({
                        target: {
                          name: 'date_end',
                          value: dateEnd,
                        },
                      })
                    }}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>
        )}
      </Formik>
    </Modal>
  )
}

export default EditAddressOverrideModal
